import is from '@sindresorhus/is';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { AgentChatMessagePage } from '../../data-contract/chat';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { chatKeys } from './query-keys';

type Options = {
    readonly enabled?: boolean;
    readonly limit?: number;
};

export const useChatThreadMessages = (
    threadId: string | undefined,
    options?: Options,
): UseInfiniteQueryResult<
    { readonly pageParams: readonly (string | undefined)[]; readonly pages: readonly AgentChatMessagePage[] },
    Error
> => {
    const { fetchChatThreadMessages } = useApplicationFetchers();

    return useInfiniteQuery<
        AgentChatMessagePage,
        Error,
        { readonly pageParams: readonly (string | undefined)[]; readonly pages: readonly AgentChatMessagePage[] },
        readonly unknown[],
        string | undefined
    >({
        enabled: !is.undefined(threadId) && options?.enabled !== false,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        initialPageParam: undefined,
        throwOnError: false,
        queryFn: async ({ pageParam }) => {
            if (is.undefined(threadId)) {
                throw new Error('threadId required');
            }

            const result = await fetchChatThreadMessages(threadId, {
                before: pageParam,
                limit: options?.limit,
            });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: chatKeys.messages(threadId ?? '', { limit: options?.limit }),
    });
};
